// language.js
const SUPPORTED_LANGUAGES = ['en', 'nl', 'pt', 'zh', 'es', 'pl'];
const DEFAULT_LANGUAGE = 'en';

function setLanguage(lang: string, flag: string) {
    // Use consistent flag mapping 
    const LANGUAGE_FLAG_MAP: Record<string, string> = {
        'en': 'gb',
        'nl': 'nl',
        'pt': 'pt',
        'zh': 'zh',
        'es': 'es',
        'pl': 'pl'
    };
    const actualFlag = LANGUAGE_FLAG_MAP[lang] || flag;
    
    localStorage.setItem('preferredLanguage', lang);
    localStorage.setItem('preferredFlag', actualFlag);
    
    // Redirect to the new language version of the current page
    const cleanUrl = window.location.pathname.replace(/^\/[a-z]{2}\//, '/');
    const newUrl = lang === 'en' 
        ? cleanUrl 
        : `/${lang}${cleanUrl}`;
    
    window.location.href = newUrl;
}

export function toggleMenu(event: Event) {
    if (event) {
        event.preventDefault();
        event.stopPropagation();
    }

    const navLinks = document.querySelector('.nav-links');
    const button = document.querySelector('.hamburger-menu');
    
    if (navLinks && button) {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        const newState = !isExpanded;
        
        button.setAttribute('aria-expanded', String(newState));
        navLinks.classList.toggle('active', newState);
    }
}

function detectUserLanguage() {
    // First check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam && SUPPORTED_LANGUAGES.includes(langParam)) {
        return langParam;
    }

    // Then check localStorage
    const storedLang = localStorage.getItem('preferredLanguage');
    if (storedLang && SUPPORTED_LANGUAGES.includes(storedLang)) {
        return storedLang;
    }

    // Finally, check browser language
    const browserLang = navigator.language;
    const langCode = browserLang.split('-')[0];
    return SUPPORTED_LANGUAGES.includes(langCode) ? langCode : DEFAULT_LANGUAGE;
}

export function initLanguagePicker() {
    const picker = document.querySelector<HTMLInputElement>('.language-picker');
    const button = document.getElementById('language-button') as HTMLInputElement;
    const options = document.querySelectorAll<HTMLInputElement>('.language-option');
    
    // Detect current page language from the HTML lang attribute
    const pageLanguage = document.documentElement.lang || 'en';
    
    // Prioritize page language over other detection methods
    const currentLang = SUPPORTED_LANGUAGES.includes(pageLanguage) ? pageLanguage : detectUserLanguage();
    
    // Update the button display
    const currentFlag = localStorage.getItem('preferredFlag') || 
        document.querySelector<HTMLInputElement>(`.language-option[data-value="${currentLang}"]`)?.dataset.flag || 
        (currentLang === 'en' ? 'gb' : currentLang);
    
    if (button) {
        const selectedFlag = document.getElementById('selected-flag') as HTMLInputElement;
        const selectedLanguage = document.getElementById('selected-language') as HTMLInputElement;
        
        if (selectedFlag && selectedLanguage) {
            selectedFlag.src = `/assets/images/flags/${currentFlag}.png`;
            selectedFlag.alt = currentLang;
            selectedLanguage.textContent = document.querySelector<HTMLInputElement>(`.language-option[data-value="${currentLang}"] span`)?.textContent || "";
        }
    }
    
    // Toggle dropdown
    button?.addEventListener('click', (e) => {
        e.stopPropagation();
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isExpanded));
        picker?.classList.toggle('open');
    });
    
    // Handle option selection
    options?.forEach(option => {
        option.addEventListener('click', () => {
            const lang = option.dataset.value || DEFAULT_LANGUAGE;
            const flag = option.dataset.flag || "gb";
            setLanguage(lang, flag);
            picker?.classList.remove('open');
            button?.setAttribute('aria-expanded', 'false');
        });
    });
    
    // Close when clicking outside
    document.addEventListener('click', (event) => {
        if (!picker?.contains(event.target as Node)) {
            picker?.classList.remove('open');
            button?.setAttribute('aria-expanded', 'false');
        }
    });
}

document.addEventListener('DOMContentLoaded', () => {
    // Handle clicks anywhere in the document
    document.addEventListener('click', (event: Event) => {
        const navLinks = document.querySelector<HTMLInputElement>('.nav-links');
        const hamburgerMenu = document.querySelector<HTMLInputElement>('.hamburger-menu');
        
        // Only process if the menu is currently open
        if (navLinks?.classList.contains('active')) {
            // Check if the click was outside both the menu and the hamburger button
            if (!navLinks.contains(event.target as Node) && !hamburgerMenu?.contains(event.target as Node)) {
                navLinks.classList.remove('active');
                hamburgerMenu?.setAttribute('aria-expanded', 'false');
            }
        }
});
});

// Make toggleMenu globally available for legacy support
declare global {
    interface Window {
        toggleMenu: (event: Event) => void;
    }
}
window.toggleMenu = toggleMenu;